import { BrandMark } from '../components/BrandMark';
import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { UserMenu } from '../components/common/UserMenu';
import { NotificationBell } from '../components/common/NotificationBell';
import { useAgentStats } from '../hooks/useAgents';
import { GettingStarted } from '../components/onboarding/GettingStarted';
import { useAgentEvents } from '../hooks/useAgentEvents';
import {
  useExecutionStats,
  useExecutions,
  getExecutionStatusColor,
  getExecutionStatusIcon,
  formatDuration,
} from '../hooks/useExecution';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { LoadingSkeleton } from '../components/common/LoadingSkeleton';
import { ConnectionStatus } from '../components/common/ConnectionStatus';
import { Execution } from '../types/execution';

export default function Dashboard() {
  const user = useAuthStore((state) => state.user);
  const navigate = useNavigate();
  const { data: agentStats, isLoading: isLoadingAgentStats } = useAgentStats();
  const { data: executionStats, isLoading: isLoadingExecutionStats } = useExecutionStats();
  const { data: recentExecutionsData, isLoading: isLoadingExecutions } = useExecutions({ limit: 10 });

  // Subscribe to agent events for real-time updates
  useAgentEvents({ showToasts: true });

  const [menuOpen, setMenuOpen] = React.useState(false);
  const logout = useAuthStore((state) => state.logout);

  const handleSignOut = async () => {
    setMenuOpen(false);
    await logout();
    navigate('/login', { replace: true });
  };

  const recentExecutions = recentExecutionsData?.data || [];

  // Brand-new = stats loaded and nothing has ever been created or run. While
  // loading we assume a returning user, so the stats never flash away.
  const statsLoaded = !isLoadingAgentStats && !isLoadingExecutionStats;
  const isNewUser =
    statsLoaded && (agentStats?.total || 0) === 0 && (executionStats?.total || 0) === 0;
  const hasRuns = !statsLoaded || (executionStats?.total || 0) > 0;

  const navLinks = [
    { to: '/dashboard', label: 'Dashboard' },
    { to: '/agents', label: 'Agents' },
    { to: '/workflows', label: 'Workflows' },
    { to: '/executions', label: 'Executions' },
    { to: '/analytics', label: 'Analytics' },
    { to: '/templates', label: 'Marketplace' },
    { to: '/organizations', label: 'Teams' },
    { to: '/schedules', label: 'Schedules' },
    { to: '/billing', label: 'Billing' },
    ...(user && (user.role === 'ADMIN' || user.role === 'SUPER_ADMIN')
      ? [{ to: '/admin', label: 'Admin' }]
      : []),
  ];

  // Calculate success rate
  const successRate = executionStats && executionStats.total > 0
    ? Math.round((executionStats.completed / executionStats.total) * 100)
    : 0;

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100">
        <div className="text-center">
          <div className="text-6xl mb-4">🤖</div>
          <p className="text-lg text-muted-foreground">Loading AgentMesh...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Navigation Header */}
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-6">
            <Link to="/dashboard" className="flex items-center space-x-2 hover:opacity-80 transition-opacity">
              <BrandMark className="h-7 w-7" />
              <span className="text-xl font-bold">AgentMesh</span>
            </Link>
            <nav className="hidden xl:flex items-center space-x-4" aria-label="Main">
              {navLinks.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className={
                    l.to === '/dashboard'
                      ? 'text-sm font-medium text-foreground'
                      : 'text-sm text-muted-foreground hover:text-foreground transition-colors'
                  }
                >
                  {l.label}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4">
            <span className="text-sm text-muted-foreground hidden 2xl:inline">
              {user.email}
            </span>
            <NotificationBell />
            {/* On phones Account / Help / Sign out live in the menu below: the
                bar overflowed 390px with them in it. */}
            <div className="hidden sm:block">
              <UserMenu />
            </div>
            <button
              type="button"
              className="xl:hidden inline-flex min-h-[2.75rem] items-center rounded-md border px-3 text-sm font-medium"
              aria-expanded={menuOpen}
              aria-controls="dashboard-mobile-nav"
              onClick={() => setMenuOpen((o) => !o)}
            >
              {menuOpen ? 'Close' : 'Menu'}
            </button>
          </div>
        </div>
        {menuOpen && (
          <nav
            id="dashboard-mobile-nav"
            aria-label="Main"
            className="xl:hidden border-t bg-white container mx-auto px-4 py-2 grid grid-cols-2 gap-1"
          >
            {navLinks.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                onClick={() => setMenuOpen(false)}
                className="min-h-[2.75rem] flex items-center rounded px-2 text-sm hover:bg-slate-100"
              >
                {l.label}
              </Link>
            ))}
            <div className="col-span-2 border-t mt-1 pt-1 grid grid-cols-2 gap-1 sm:hidden">
              <Link
                to="/account"
                onClick={() => setMenuOpen(false)}
                className="min-h-[2.75rem] flex items-center rounded px-2 text-sm hover:bg-slate-100"
              >
                Account
              </Link>
              <Link
                to="/help"
                onClick={() => setMenuOpen(false)}
                className="min-h-[2.75rem] flex items-center rounded px-2 text-sm hover:bg-slate-100"
              >
                Help
              </Link>
              <button
                type="button"
                onClick={handleSignOut}
                className="min-h-[2.75rem] flex items-center rounded px-2 text-sm text-left hover:bg-slate-100"
              >
                Sign out
              </button>
            </div>
          </nav>
        )}
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold mb-2">
            {isNewUser
              ? `Welcome to AgentMesh${user.firstName ? `, ${user.firstName}` : ''}`
              : `Welcome back${user.firstName ? `, ${user.firstName}` : ''}!`}
          </h2>
          <p className="text-muted-foreground">
            {isNewUser
              ? 'Start with one agent that does one real job. You will see every step it takes and what each run costs.'
              : 'Your agents, their runs, and what they cost.'}
          </p>
        </div>

        <GettingStarted />

        {/* Stats, chart and recent runs only once there is something to show —
            a new user's first screen was four zeros and an empty chart. */}
        {hasRuns && (
        <>
        {/* Execution Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center space-x-2 text-sm font-medium">
                <span>📊</span>
                <span>Total Executions</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingExecutionStats ? (
                <LoadingSkeleton className="h-10 w-16" />
              ) : (
                <>
                  <div className="text-4xl font-bold">{executionStats?.total || 0}</div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {executionStats?.running || 0} currently running
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center space-x-2 text-sm font-medium">
                <span>✅</span>
                <span>Success Rate</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingExecutionStats ? (
                <LoadingSkeleton className="h-10 w-16" />
              ) : (
                <>
                  <div className="text-4xl font-bold text-brand">{successRate}%</div>
                  <p className="text-sm text-muted-foreground mt-1">
                    {executionStats?.completed || 0} completed, {executionStats?.failed || 0} failed
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center space-x-2 text-sm font-medium">
                <span>⏱️</span>
                <span>Avg Duration</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingExecutionStats ? (
                <LoadingSkeleton className="h-10 w-16" />
              ) : (
                <>
                  <div className="text-4xl font-bold">
                    {executionStats?.averageDuration ? formatDuration(executionStats.averageDuration) : '-'}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">
                    average execution time
                  </p>
                </>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="flex items-center space-x-2 text-sm font-medium">
                <span>🤖</span>
                <span>Active Agents</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {isLoadingAgentStats ? (
                <LoadingSkeleton className="h-10 w-16" />
              ) : (
                <>
                  <div className="text-4xl font-bold text-brand">{agentStats?.active || 0}</div>
                  <p className="text-sm text-muted-foreground mt-1">
                    of {agentStats?.total || 0} total agents
                  </p>
                </>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Execution Chart and Recent Executions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
          {/* Execution Chart */}
          <ExecutionChart executions={recentExecutions} isLoading={isLoadingExecutions} />

          {/* Recent Executions */}
          {/* data-testid: the e2e suite matched `ul, table` filtered by text and
              found nothing, because this renders as cards. */}
          <Card data-testid="recent-executions">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Recent Executions</CardTitle>
                  <CardDescription>Last 10 executions across all agents</CardDescription>
                </div>
                <Button variant="outline" size="sm" asChild>
                  <Link to="/executions">View All</Link>
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {isLoadingExecutions ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4, 5].map((i) => (
                    <LoadingSkeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : recentExecutions.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No executions yet.</p>
                  <p className="text-sm">Run an agent to see execution history.</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[320px] overflow-y-auto">
                  {recentExecutions.map((execution) => (
                    <Link
                      key={execution.id}
                      to={`/executions/${execution.id}`}
                      className="block"
                    >
                      <div className="flex items-center justify-between p-2 rounded-lg border hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-2">
                          <span className="text-lg">
                            {getExecutionStatusIcon(execution.status)}
                          </span>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate max-w-[150px]">
                              {execution.agentName}
                            </p>
                            <p className="text-xs text-muted-foreground">
                              {new Date(execution.createdAt).toLocaleString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2 py-0.5 text-xs rounded-full font-medium ${getExecutionStatusColor(
                              execution.status
                            )}`}
                          >
                            {execution.status}
                          </span>
                          {execution.duration !== null && (
                            <span className="text-xs text-muted-foreground">
                              {formatDuration(execution.duration)}
                            </span>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Agent Performance Section */}
        <AgentPerformance executions={recentExecutions} isLoading={isLoadingExecutions} />
        </>
        )}

        {/* Agent Type Distribution Chart */}
        {!isLoadingAgentStats && agentStats && agentStats.total > 0 && (
          <div className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle>Agent Type Distribution</CardTitle>
                <CardDescription>
                  Breakdown of agents by type
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <DistributionBar
                    icon="💬"
                    label="Customer Support"
                    count={agentStats.byType?.['CUSTOMER_SUPPORT'] || 0}
                    total={agentStats.total}
                    color="bg-blue-600"
                  />
                  <DistributionBar
                    icon="📊"
                    label="Data Processing"
                    count={agentStats.byType?.['DATA_PROCESSING'] || 0}
                    total={agentStats.total}
                    color="bg-green-600"
                  />
                  <DistributionBar
                    icon="⚙️"
                    label="Workflow Automation"
                    count={agentStats.byType?.['WORKFLOW_AUTOMATION'] || 0}
                    total={agentStats.total}
                    color="bg-purple-600"
                  />
                  <DistributionBar
                    icon="✍️"
                    label="Content Generation"
                    count={agentStats.byType?.['CONTENT_GENERATION'] || 0}
                    total={agentStats.total}
                    color="bg-pink-600"
                  />
                  <DistributionBar
                    icon="🔍"
                    label="Analysis"
                    count={agentStats.byType?.['ANALYSIS'] || 0}
                    total={agentStats.total}
                    color="bg-cyan-600"
                  />
                  <DistributionBar
                    icon="🔌"
                    label="Integration"
                    count={agentStats.byType?.['INTEGRATION'] || 0}
                    total={agentStats.total}
                    color="bg-amber-600"
                  />
                  <DistributionBar
                    icon="🔧"
                    label="Custom"
                    count={agentStats.byType?.['CUSTOM'] || 0}
                    total={agentStats.total}
                    color="bg-orange-600"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        <div className="mt-8">
          <Card>
            <CardHeader>
              <CardTitle>Quick Actions</CardTitle>
              <CardDescription>
                Manage your agents and workflows
              </CardDescription>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-3">
              <Button
                className="justify-start"
                variant="outline"
                onClick={() => navigate('/agents/new')}
              >
                <span className="mr-2">+</span>
                Create Agent
              </Button>
              <Button
                className="justify-start"
                variant="outline"
                onClick={() => navigate('/agents')}
              >
                <span className="mr-2">🤖</span>
                View Agents
              </Button>
              <Button
                className="justify-start"
                variant="outline"
                onClick={() => navigate('/workflows')}
              >
                <span className="mr-2">🔄</span>
                Workflows
              </Button>
              <Button
                className="justify-start"
                variant="outline"
                onClick={() => navigate('/executions')}
              >
                <span className="mr-2">📋</span>
                Executions
              </Button>
              <Button
                className="justify-start"
                variant="outline"
                onClick={() => navigate('/templates')}
              >
                <span className="mr-2">🛒</span>
                Marketplace
              </Button>
            </CardContent>
          </Card>
        </div>
      </main>

      {/* WebSocket connection status indicator */}
      <ConnectionStatus />
    </div>
  );
}

interface DistributionBarProps {
  icon: string;
  label: string;
  count: number;
  total: number;
  color: string;
}

function DistributionBar({ icon, label, count, total, color }: DistributionBarProps) {
  const percentage = total > 0 ? (count / total) * 100 : 0;

  if (count === 0) return null;

  return (
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <span>{icon}</span>
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="flex items-center gap-2">
        <div className="w-32 bg-slate-200 rounded-full h-2 overflow-hidden">
          <div
            className={`${color} h-2 transition-all duration-300`}
            style={{ width: `${percentage}%` }}
          />
        </div>
        <span className="text-sm font-bold w-8 text-right">{count}</span>
      </div>
    </div>
  );
}

/**
 * Execution Chart Component
 * Displays a CSS bar chart of executions over the last 7 days
 */
interface ExecutionChartProps {
  executions: Execution[];
  isLoading: boolean;
}

function ExecutionChart({ executions, isLoading }: ExecutionChartProps) {
  // Group executions by day for the last 7 days
  const chartData = React.useMemo(() => {
    const days: { date: string; label: string; completed: number; failed: number; other: number }[] = [];
    const now = new Date();

    for (let i = 6; i >= 0; i--) {
      const date = new Date(now);
      date.setDate(date.getDate() - i);
      const dateStr = date.toISOString().split('T')[0];
      const label = date.toLocaleDateString('en-US', { weekday: 'short' });

      days.push({
        date: dateStr,
        label,
        completed: 0,
        failed: 0,
        other: 0,
      });
    }

    // Count executions per day
    executions.forEach((execution) => {
      const execDate = execution.createdAt.split('T')[0];
      const dayData = days.find((d) => d.date === execDate);
      if (dayData) {
        if (execution.status === 'COMPLETED') {
          dayData.completed++;
        } else if (execution.status === 'FAILED') {
          dayData.failed++;
        } else {
          dayData.other++;
        }
      }
    });

    return days;
  }, [executions]);

  // Find max for scaling
  const maxCount = Math.max(
    1,
    ...chartData.map((d) => d.completed + d.failed + d.other)
  );

  return (
    // data-testid: this chart is hand-built from divs, so the e2e suite's
    // `canvas, svg[class*="chart"]` never matched it.
    <Card data-testid="executions-chart">
      <CardHeader className="pb-2">
        <CardTitle>Executions Over Time</CardTitle>
        <CardDescription>Last 7 days activity</CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="h-[200px] flex items-center justify-center">
            <LoadingSkeleton className="h-full w-full" />
          </div>
        ) : (
          <div className="h-[200px] flex items-end justify-between gap-2">
            {chartData.map((day) => {
              const total = day.completed + day.failed + day.other;
              const height = total > 0 ? (total / maxCount) * 100 : 0;
              const completedHeight = day.completed > 0 ? (day.completed / total) * height : 0;
              const failedHeight = day.failed > 0 ? (day.failed / total) * height : 0;
              const otherHeight = day.other > 0 ? (day.other / total) * height : 0;

              return (
                <div key={day.date} className="flex-1 flex flex-col items-center">
                  <div
                    className="w-full flex flex-col-reverse rounded-t-sm overflow-hidden"
                    style={{ height: '160px' }}
                  >
                    {total > 0 ? (
                      <>
                        <div
                          className="bg-green-500 w-full transition-all duration-300"
                          style={{ height: `${completedHeight}%` }}
                          title={`${day.completed} completed`}
                        />
                        <div
                          className="bg-red-500 w-full transition-all duration-300"
                          style={{ height: `${failedHeight}%` }}
                          title={`${day.failed} failed`}
                        />
                        <div
                          className="bg-blue-400 w-full transition-all duration-300"
                          style={{ height: `${otherHeight}%` }}
                          title={`${day.other} other`}
                        />
                      </>
                    ) : (
                      <div className="bg-slate-100 w-full h-1 rounded-t-sm" />
                    )}
                  </div>
                  <div className="mt-2 text-xs text-muted-foreground">{day.label}</div>
                  <div className="text-xs font-medium">{total > 0 ? total : '-'}</div>
                </div>
              );
            })}
          </div>
        )}
        {/* Legend */}
        <div className="flex items-center justify-center gap-4 mt-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-green-500 rounded" />
            <span>Completed</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-red-500 rounded" />
            <span>Failed</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 bg-blue-400 rounded" />
            <span>Other</span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * Agent Performance Component
 * Shows top agents by execution count
 */
interface AgentPerformanceProps {
  executions: Execution[];
  isLoading: boolean;
}

function AgentPerformance({ executions, isLoading }: AgentPerformanceProps) {
  // Aggregate execution counts by agent
  const agentPerformance = React.useMemo(() => {
    const agentMap = new Map<
      string,
      {
        agentId: string;
        agentName: string;
        total: number;
        completed: number;
        failed: number;
        avgDuration: number;
      }
    >();

    executions.forEach((execution) => {
      const existing = agentMap.get(execution.agentId);
      if (existing) {
        existing.total++;
        if (execution.status === 'COMPLETED') existing.completed++;
        if (execution.status === 'FAILED') existing.failed++;
        if (execution.duration) {
          existing.avgDuration =
            (existing.avgDuration * (existing.total - 1) + execution.duration) / existing.total;
        }
      } else {
        agentMap.set(execution.agentId, {
          agentId: execution.agentId,
          agentName: execution.agentName,
          total: 1,
          completed: execution.status === 'COMPLETED' ? 1 : 0,
          failed: execution.status === 'FAILED' ? 1 : 0,
          avgDuration: execution.duration || 0,
        });
      }
    });

    return Array.from(agentMap.values())
      .sort((a, b) => b.total - a.total)
      .slice(0, 5);
  }, [executions]);

  if (isLoading) {
    return (
      <div className="mt-6">
        <Card>
          <CardHeader>
            <CardTitle>Agent Performance</CardTitle>
            <CardDescription>Top agents by execution count</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {[1, 2, 3].map((i) => (
                <LoadingSkeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (agentPerformance.length === 0) {
    return null;
  }

  const maxTotal = Math.max(...agentPerformance.map((a) => a.total));

  return (
    <div className="mt-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Agent Performance</CardTitle>
              <CardDescription>Top agents by execution count</CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {agentPerformance.map((agent, index) => {
              const successRate =
                agent.total > 0 ? Math.round((agent.completed / agent.total) * 100) : 0;
              const barWidth = (agent.total / maxTotal) * 100;

              return (
                <Link
                  key={agent.agentId}
                  to={`/agents/${agent.agentId}`}
                  className="block"
                >
                  <div className="p-3 rounded-lg border hover:bg-slate-50 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-bold text-muted-foreground">
                          #{index + 1}
                        </span>
                        <span className="font-medium">{agent.agentName}</span>
                      </div>
                      <div className="flex items-center gap-3 text-sm">
                        <span className="text-muted-foreground">
                          {agent.total} runs
                        </span>
                        <span
                          className={`font-medium ${
                            successRate >= 80
                              ? 'text-brand'
                              : successRate >= 50
                              ? 'text-yellow-600'
                              : 'text-red-600'
                          }`}
                        >
                          {successRate}% success
                        </span>
                      </div>
                    </div>
                    <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-blue-500 to-blue-600 transition-all duration-300"
                        style={{ width: `${barWidth}%` }}
                      />
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
